import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import type { PopoverHandle } from '@humanforce/mango-ui-react';

interface DismissableOverlay {
  hide: () => void;
}

interface PopoverGroupContextValue {
  notifyOpen: (id: string, handle: DismissableOverlay) => void;
  notifyClose: (id: string) => void;
}

const noopGroup: PopoverGroupContextValue = {
  notifyOpen: () => undefined,
  notifyClose: () => undefined,
};

const PopoverGroupContext = createContext<PopoverGroupContextValue>(noopGroup);

export const PopoverGroupProvider = ({ children }: { children: ReactNode }) => {
  const openRef = useRef<{ id: string; handle: DismissableOverlay } | null>(null);

  const value = useMemo<PopoverGroupContextValue>(() => ({
    notifyOpen: (id, handle) => {
      if (openRef.current && openRef.current.id !== id) openRef.current.handle.hide();
      openRef.current = { id, handle };
    },
    notifyClose: (id) => {
      if (openRef.current?.id === id) openRef.current = null;
    },
  }), []);

  return <PopoverGroupContext.Provider value={value}>{children}</PopoverGroupContext.Provider>;
};

export const useExclusivePopover = (id: string) => {
  const group = useContext(PopoverGroupContext);
  const ref = useRef<PopoverHandle>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const openedByHoverRef = useRef(false);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current !== null) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);

  const onShow = useCallback(() => {
    isOpenRef.current = true;
    setIsOpen(true);
    group.notifyOpen(id, { hide: () => ref.current?.hide() });
  }, [group, id]);

  const onHide = useCallback(() => {
    clearHoverCloseTimeout();
    isOpenRef.current = false;
    openedByHoverRef.current = false;
    setIsOpen(false);
    group.notifyClose(id);
  }, [clearHoverCloseTimeout, group, id]);

  const closeHoverPopover = useCallback(() => {
    clearHoverCloseTimeout();
    if (!openedByHoverRef.current) return;
    openedByHoverRef.current = false;
    ref.current?.hide();
  }, [clearHoverCloseTimeout]);

  const onHoverEnter = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    clearHoverCloseTimeout();
    if (isOpenRef.current) return;
    openedByHoverRef.current = true;
    ref.current?.show(event, event.currentTarget);
  }, [clearHoverCloseTimeout]);

  const onHoverLeave = useCallback(() => {
    if (!openedByHoverRef.current) return;
    clearHoverCloseTimeout();
    hoverCloseTimeoutRef.current = setTimeout(closeHoverPopover, 150);
  }, [clearHoverCloseTimeout, closeHoverPopover]);

  const onPopoverEnter = useCallback(() => {
    clearHoverCloseTimeout();
  }, [clearHoverCloseTimeout]);

  const onHoverClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!openedByHoverRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => clearHoverCloseTimeout, [clearHoverCloseTimeout]);

  return { ref, isOpen, onShow, onHide, onHoverEnter, onHoverLeave, onPopoverEnter, onHoverClickCapture };
};

export const useExclusiveOverlay = (id: string) => {
  const group = useContext(PopoverGroupContext);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const openedByHoverRef = useRef(false);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideRef = useRef<() => void>(() => undefined);

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current !== null) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearHoverCloseTimeout();
    if (!isOpenRef.current) return;
    openedByHoverRef.current = false;
    isOpenRef.current = false;
    setIsOpen(false);
    group.notifyClose(id);
  }, [clearHoverCloseTimeout, group, id]);
  hideRef.current = hide;

  const handle = useMemo<DismissableOverlay>(() => ({ hide: () => hideRef.current() }), []);

  const show = useCallback(() => {
    clearHoverCloseTimeout();
    if (isOpenRef.current) return;
    isOpenRef.current = true;
    setIsOpen(true);
    group.notifyOpen(id, handle);
  }, [clearHoverCloseTimeout, group, handle, id]);

  const showByHover = useCallback(() => {
    openedByHoverRef.current = true;
    show();
  }, [show]);

  const scheduleHide = useCallback(() => {
    clearHoverCloseTimeout();
    hoverCloseTimeoutRef.current = setTimeout(hide, 150);
  }, [clearHoverCloseTimeout, hide]);

  const toggle = useCallback(() => {
    if (openedByHoverRef.current) {
      openedByHoverRef.current = false;
      return;
    }
    if (isOpenRef.current) hide();
    else show();
  }, [hide, show]);

  useEffect(() => () => {
    clearHoverCloseTimeout();
    group.notifyClose(id);
  }, [clearHoverCloseTimeout, group, id]);

  return { isOpen, showByHover, hide, toggle, scheduleHide, cancelScheduledHide: clearHoverCloseTimeout };
};
